import type { AdminWhatsappSystemInstance } from '../../../domain/superadmin/types';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import Switch from '../../../components/ui/Switch';

type Props = {
    instance: AdminWhatsappSystemInstance | null;
    isLoading: boolean;
    instanceError?: string | null;
    settingsError?: string | null;
    commsEnabled: boolean;
    onCommsChange: (enabled: boolean) => void;
    onConnect: () => void;
    onReconnect: () => void;
    onDisconnect: () => void;
    isMutating?: boolean;
};

function statusMeta(instance: AdminWhatsappSystemInstance | null) {
    if (!instance?.configured) {
        return { label: 'Não configurado', dotClass: 'bg-muted' };
    }
    if (instance.connected) {
        return { label: 'Conectado', dotClass: 'bg-[var(--color-success-500,#22c55e)]' };
    }
    return { label: 'Desconectado', dotClass: 'bg-amber-500' };
}

export function WhatsappSystemInstancePanel({
    instance,
    isLoading,
    instanceError = null,
    settingsError = null,
    commsEnabled,
    onCommsChange,
    onConnect,
    onReconnect,
    onDisconnect,
    isMutating = false,
}: Props) {
    const status = statusMeta(instance);
    const configured = Boolean(instance?.configured);
    const connected = Boolean(instance?.connected);

    return (
        <section
            aria-label="Instância de sistema"
            className="mb-s4 rounded-xl border border-subtle bg-card p-4 sm:p-5"
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        <h2 className="text-base font-semibold text-default">Instância de sistema</h2>
                        {isLoading ? (
                            <div className="mt-2 flex items-center gap-2 text-sm text-muted">
                                <Spinner color="primary" size="sm" />
                                Carregando status…
                            </div>
                        ) : instanceError ? null : (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-secondary">
                                <span className="inline-flex items-center gap-1.5">
                                    <span
                                        className={`h-2 w-2 rounded-full ${status.dotClass}`}
                                        aria-hidden
                                    />
                                    {status.label}
                                </span>
                                {instance?.instanceName && (
                                    <span className="truncate">{instance.instanceName}</span>
                                )}
                                {instance?.phone && (
                                    <span className="truncate font-mono text-muted">{instance.phone}</span>
                                )}
                            </div>
                        )}
                    </div>

                    {!isLoading && !instanceError && (
                        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                            {!configured && (
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={onConnect}
                                    disabled={isMutating}
                                    className="w-full sm:w-auto"
                                >
                                    {isMutating ? 'Conectando…' : 'Conectar WhatsApp'}
                                </Button>
                            )}
                            {configured && !connected && (
                                <>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={onReconnect}
                                        disabled={isMutating}
                                        className="w-full sm:w-auto"
                                    >
                                        {isMutating ? 'Reconectando…' : 'Reconectar'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={onDisconnect}
                                        disabled={isMutating}
                                        className="w-full sm:w-auto"
                                    >
                                        {isMutating ? 'Desconectando…' : 'Desconectar'}
                                    </Button>
                                </>
                            )}
                            {configured && connected && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={onDisconnect}
                                    disabled={isMutating}
                                    className="w-full sm:w-auto"
                                >
                                    {isMutating ? 'Desconectando…' : 'Desconectar'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {!configured && !instanceError && (
                    <Alert className="border-amber-500/50 text-amber-600 dark:text-amber-400">
                        <AlertTitle>Nenhuma instância conectada</AlertTitle>
                        <AlertDescription>
                            Clique em Conectar WhatsApp para vincular um número à comunicação do
                            sistema.
                        </AlertDescription>
                    </Alert>
                )}

                {!isLoading && !settingsError && (
                    <Switch
                        checked={commsEnabled}
                        onChange={onCommsChange}
                        label="Comunicação do sistema"
                        description="Habilita envios de WhatsApp pela instância de sistema"
                        disabled={isMutating}
                    />
                )}
            </div>
        </section>
    );
}
